/**
 * Progress Step Item
 * Renders a step indicator with icon and content for progress tracking
 */

import { Text, Spinner, makeStyles, tokens, mergeClasses } from '@fluentui/react-components'
import { CheckmarkCircleRegular, CircleRegular, CircleSmallFilled } from '@fluentui/react-icons'
import type { InsightContentItem, StepStatus } from '../../../../domain'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
    position: 'relative',
    paddingLeft: tokens.spacingHorizontalXS,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    flexShrink: 0,
  },
  completedIcon: {
    color: tokens.colorPaletteGreenForeground1,
  },
  inProgressIcon: {
    color: tokens.colorBrandForeground1,
  },
  pendingIcon: {
    color: tokens.colorNeutralForeground4,
  },
  skippedIcon: {
    color: tokens.colorNeutralForeground4,
    textDecoration: 'line-through',
  },
  content: {
    flex: 1,
    lineHeight: '20px',
  },
  completedContent: {
    color: tokens.colorNeutralForeground2,
  },
  inProgressContent: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  pendingContent: {
    color: tokens.colorNeutralForeground3,
  },
  skippedContent: {
    color: tokens.colorNeutralForeground4,
    textDecoration: 'line-through',
  },
  stepNumber: {
    color: tokens.colorNeutralForeground3,
    marginRight: tokens.spacingHorizontalXS,
  },
})

export interface ProgressStepItemProps {
  item: InsightContentItem
}

function getStatusIcon(status: StepStatus, styles: ReturnType<typeof useStyles>) {
  switch (status) {
    case 'completed':
      return <CheckmarkCircleRegular className={styles.completedIcon} />
    case 'in_progress':
      return <Spinner size="tiny" className={styles.inProgressIcon} />
    case 'skipped':
      return <CircleSmallFilled className={styles.skippedIcon} />
    case 'pending':
    default:
      return <CircleRegular className={styles.pendingIcon} />
  }
}

export function ProgressStepItem({ item }: ProgressStepItemProps) {
  const styles = useStyles()
  const status = item.stepStatus ?? 'pending'

  const contentClass = mergeClasses(
    styles.content,
    status === 'completed' && styles.completedContent,
    status === 'in_progress' && styles.inProgressContent,
    status === 'pending' && styles.pendingContent,
    status === 'skipped' && styles.skippedContent
  )

  return (
    <div className={styles.root}>
      <span className={styles.iconContainer}>{getStatusIcon(status, styles)}</span>
      <Text className={contentClass}>
        {item.stepNumber !== undefined && <span className={styles.stepNumber}>{item.stepNumber}.</span>}
        {item.content}
      </Text>
    </div>
  )
}
